/**
 * ApexCharts falha de forma benigna quando o alvo DOM some (navegação, v-if):
 * - Promise rejeitada com Error('Element not found')
 * - TypeError em baseEl.querySelector quando handlers (ex.: mousedown) ainda correm
 */

export function apexChartsErrorMessage(reason) {
    if (reason == null) return '';
    if (typeof reason === 'string') return reason;
    if (typeof reason === 'object' && typeof reason.message === 'string') {
        return reason.message;
    }
    return String(reason);
}

/** @deprecated Prefer isBenignApexChartsNavigationReason */
export function apexChartsElementNotFoundMessage(reason) {
    return apexChartsErrorMessage(reason);
}

export function isApexChartsElementNotFoundReason(reason) {
    return apexChartsErrorMessage(reason).includes('Element not found');
}

export function isApexChartsDomGoneQuerySelectorReason(reason) {
    const msg = apexChartsErrorMessage(reason).toLowerCase();
    if (!msg.includes('queryselector') && !msg.includes('baseel')) {
        return false;
    }

    return (
        /cannot read properties of null \(reading 'queryselector'\)/.test(msg) ||
        /null is not an object \(evaluating .*(baseel|queryselector)/.test(msg) ||
        /undefined is not an object \(evaluating .*(baseel|queryselector)/.test(msg)
    );
}

export function isBenignApexChartsNavigationReason(reason) {
    return (
        isApexChartsElementNotFoundReason(reason) ||
        isApexChartsDomGoneQuerySelectorReason(reason)
    );
}

/**
 * @param {import('@sentry/core').ErrorEvent | undefined} event
 */
function hasApexChartsStackFrame(event) {
    const values = event?.exception?.values ?? [];
    for (const value of values) {
        const frames = value?.stacktrace?.frames ?? [];
        if (
            frames.some((frame) => {
                const loc = `${frame.module || ''} ${frame.filename || ''}`;
                return loc.includes('apexcharts');
            })
        ) {
            return true;
        }
    }
    return false;
}

function messageFromSentryHint(hint) {
    const ex = hint?.originalException;
    if (typeof ex === 'string') return ex;
    if (ex && typeof ex === 'object') {
        if (typeof ex.message === 'string') return ex.message;
        if (ex.reason && typeof ex.reason.message === 'string') return ex.reason.message;
    }
    return '';
}

/**
 * Ruído de navegação / desmontagem do ApexCharts para beforeSend do Sentry.
 * @param {import('@sentry/core').ErrorEvent} event
 * @param {import('@sentry/core').EventHint | undefined} hint
 */
export function isBenignApexChartsNavigationNoise(event, hint) {
    if (isBenignApexChartsNavigationReason(hint?.originalException)) {
        return true;
    }

    const fromHint = messageFromSentryHint(hint);
    const fromEvent = event?.exception?.values?.map((v) => v?.value).filter(Boolean).join(' ') || '';
    const msg = `${fromHint} ${fromEvent}`;

    if (msg.includes('Element not found')) {
        return true;
    }

    if (isApexChartsDomGoneQuerySelectorReason({ message: msg }) && hasApexChartsStackFrame(event)) {
        return true;
    }

    return false;
}

function reasonFromGlobalErrorEvent(event) {
    if (event?.error != null) return event.error;
    if (typeof event?.message === 'string') return event.message;
    return null;
}

/**
 * Evita que erros síncronos benignos (ex.: mousedown após destroy) cheguem ao Sentry/console.
 * Deve ser registado antes de Sentry.init.
 */
export function installApexChartsGlobalErrorGuard() {
    if (typeof window === 'undefined') return;

    window.addEventListener(
        'error',
        (event) => {
            if (!isBenignApexChartsNavigationReason(reasonFromGlobalErrorEvent(event))) {
                return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
        },
        true
    );
}

/**
 * Evita que rejeições benignas cheguem ao handler global do Sentry.
 * Deve ser registado antes de Sentry.init.
 */
export function installApexChartsUnhandledRejectionGuard() {
    if (typeof window === 'undefined') return;

    window.addEventListener(
        'unhandledrejection',
        (event) => {
            if (isBenignApexChartsNavigationReason(event.reason)) {
                event.preventDefault();
            }
        },
        true
    );
}

/** Regista todos os guards de ruído benigno do ApexCharts. */
export function installApexChartsBenignErrorGuards() {
    installApexChartsGlobalErrorGuard();
    installApexChartsUnhandledRejectionGuard();
}

/** Aguarda paint estável antes de montar gráficos (mitiga race no mobile). */
export function waitForChartDomReady() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
}
